import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Divider, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material'
import { TextField } from 'src/Libs/inputs/formik'
import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ShippingMethod } from '../../types'
import { useFormikContext } from 'formik'
import { ShippingFormType } from 'src/Libs/shop'
import Select from 'src/Libs/inputs/formik/Select'
import { shopPhoneNumberPrefixes } from '../../utils'
import { useEffectShopAccess, useShopCart } from '../../hooks'
import { ShippingMethodResponseModel } from '../../models/shipping-method-modal.model'
import { useShopCartContext } from '../../context'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { SHOP_QNTM_DELIVERY_PICK_SHIPPING_METHOD_ID } from '../../consts'
import ConfirmCheckoutDeliveryPickUpload from './ConfirmCheckoutDeliveryPickUpload'

interface ConfirmCheckoutShippingMethodProps {
  selectedShippingMethodId: string
}

const useStyles = makeStyles()(theme => ({
  container: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    minHeight: '300px',
  },
  normalText: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.grey.grey03,
  },
}))

const ConfirmCheckoutShippingMethod: React.FC<ConfirmCheckoutShippingMethodProps> = ({ selectedShippingMethodId }) => {
  const { classes } = useStyles()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { t } = useCustomTranslation()
  const { getCheckoutProducts } = useShopCart()
  const { setCartData } = useShopCartContext()
  const { getShippingMethods, setShippingMethod } = useShopCart()
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodResponseModel | null>()
  const { handleSubmit, values, setFieldValue } = useFormikContext<ShippingFormType>()
  const avisFields = values.shippingMethod === ShippingMethod.Avis || values.shippingMethod === ShippingMethod.QntmSelfPick
  const requirePdfUpload = values.shippingMethod === SHOP_QNTM_DELIVERY_PICK_SHIPPING_METHOD_ID
  const formattedPhoneNumberPrefixes = shopPhoneNumberPrefixes.map(prefix => ({
    label: `${prefix.code} (${prefix.iso})`,
    value: prefix.code,
  }))

  const fetchShippingMethodDetails = async (shippingMethodId: string): Promise<void> => {
    try {
      setIsLoading(true)
      setShippingMethod(shippingMethodId).then(() => {
        getCheckoutProducts()
          .then(result => {
            setCartData(result)
          })
          .finally(() => {
            setIsLoading(false)
          })
      })
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    setFieldValue('shippingMethod', selectedShippingMethodId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShippingMethodId])

  useEffectShopAccess(() => {
    getShippingMethods().then(result => {
      setShippingMethods(result)
    })
  }, [])

  return (
    <Grid item xs={12} md={6} className={classes.container}>
      <Typography variant="h2" sx={{ fontWeight: 400 }} className={classes.normalText}>
        {t('shop.confirm-checkout.shipping-method')}
      </Typography>
      <Divider className={classes.normalText} variant="fullWidth" />
      <form onSubmit={handleSubmit}>
        {isLoading ? (
          <Box sx={{ mt: 3 }}>
            <Loading />
          </Box>
        ) : (
          <>
            <RadioGroup
              name="shippingMethod"
              value={values.shippingMethod}
              onChange={event => {
                fetchShippingMethodDetails(event.target.value)
              }}
            >
              {shippingMethods?.elements.map(shippingMethod => {
                return (
                  <FormControlLabel
                    key={shippingMethod.id}
                    value={shippingMethod.id}
                    control={<Radio color="primary" />}
                    label={shippingMethod.translated.name}
                  />
                )
              })}
            </RadioGroup>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2 }}>
                {avisFields && (
                  <>
                    <TextField name="contactPerson" label={t('shop.shipping-method.contact-person')} required={avisFields} fullWidth />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ width: 'minWidth' }}>
                        <Select
                          isSearchable
                          required={avisFields}
                          options={formattedPhoneNumberPrefixes}
                          label={t('shop.shipping-method.phone-prefix')}
                          name={'phonePrefix'}
                        />
                      </Box>
                      <TextField name="phoneNumber" label={t('shop.shipping-method.contact-phone-number')} required={avisFields} fullWidth />
                    </Box>
                    <TextField name="comment" label={t('shop.shipping-method.comment')} fullWidth />
                  </>
                )}
                {requirePdfUpload && <ConfirmCheckoutDeliveryPickUpload />}
              </Box>
            </Box>
          </>
        )}
      </form>
    </Grid>
  )
}

export default ConfirmCheckoutShippingMethod
